import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.data.profile');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

interface CoverageMetric {
    label: string;
    description: string;
    value: number;
}

interface DistributionItem {
    label: string;
    count: number;
}

@Component({
    selector: 'wiz-page-data-profile',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.data.profile/view.scss */
:host {
  display: block;
  min-height: 100%;
}

.source-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.source-text-expanded {
  max-height: 32rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.field-sample {
  max-width: 34rem;
}`],
})
export class PageDataProfileComponent implements OnInit {
    public loading: boolean = true;
    public errorMessage: string = '';
    public profile: any = {};
    public fields: any[] = [];
    public coverageMetrics: CoverageMetric[] = [];
    public languages: DistributionItem[] = [];
    public countries: DistributionItem[] = [];
    public samples: any[] = [];
    public lastUpdated: string = '';
    public sources: any[] = [];
    public selectedSourceId: string = 'nahf-document-archive';
    public expandedTexts: { [key: string]: boolean } = {};

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.load();
    }

    public async load() {
        this.loading = true;
        this.errorMessage = '';
        await this.service.render();

        try {
            const response = await wiz.call("overview", {
                source_id: this.selectedSourceId
            });
            if (response.code !== 200) {
                this.errorMessage = this.responseMessage(
                    response.data,
                    "데이터 프로파일을 불러오지 못했습니다."
                );
                return;
            }

            this.sources = this.toArray(response.data?.sources);
            const payload = response.data?.profile || {};
            this.profile = payload;
            this.fields = this.normalizeFields(
                payload.fields || payload.field_profiles || payload.schema
            );
            this.coverageMetrics = this.normalizeCoverage(
                payload.coverage || payload.completeness || payload.statistics?.coverage
            );

            const distributions = payload.distributions || payload.distribution || {};
            this.languages = this.normalizeDistribution(
                distributions.languages
                    || distributions.language
                    || payload.language_distribution
                    || payload.languages
            );
            this.countries = this.normalizeDistribution(
                distributions.publication_countries
                    || distributions.countries
                    || distributions.publication_country
                    || payload.country_distribution
            );
            this.samples = this.toArray(
                payload.samples
                    || payload.sample_records
                    || payload.sample_chunks
                    || payload.statistics?.samples
            );
            this.lastUpdated = '';
            const profileDate = payload.created_at ? new Date(payload.created_at) : null;
            if (profileDate && !Number.isNaN(profileDate.getTime())) {
                this.lastUpdated = profileDate.toLocaleString('ko-KR', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } else {
                this.lastUpdated = payload.created_at ? String(payload.created_at) : '';
            }
        } catch (_error) {
            this.errorMessage = "데이터 프로파일을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.";
        } finally {
            this.loading = false;
            await this.service.render();
        }
    }

    public async selectSource(source: any) {
        const sourceId = String(source?.id || '').trim();
        if (!sourceId || sourceId === this.selectedSourceId) return;
        this.selectedSourceId = sourceId;
        this.expandedTexts = {};
        await this.load();
    }

    public isSourceSelected(source: any): boolean {
        return String(source?.id || '') === this.selectedSourceId;
    }

    public sourceName(source: any): string {
        return source?.name || source?.id || '데이터 소스';
    }

    public get isValidationExcerptProfile(): boolean {
        return [
            'metadata_and_bounded_excerpts',
            'metadata_and_search_excerpts',
            'mixed_full_text_and_search_excerpts'
        ].includes(String(this.profile?.ingestion_scope || ''));
    }

    public get isCompleteCorpusProfile(): boolean {
        return this.profile?.complete_corpus === true
            && String(this.profile?.ingestion_scope || '') === 'complete_versioned_source_corpus';
    }

    public get totalRecords(): number {
        return this.summaryCount(
            ["total_records", "records", "record_count", "total_chunks", "chunks", "chunk_count"],
            this.samples.length
        );
    }

    public get totalDocuments(): number {
        return this.summaryCount(
            ["total_documents", "documents", "document_count"],
            0
        );
    }

    public get totalChunks(): number {
        return this.summaryCount(
            ["total_chunks", "chunks", "chunk_count", "total_records", "records"],
            this.samples.length
        );
    }

    public get averageChunkLength(): number {
        return this.pickNumber(
            [
                this.profile?.average_chunk_length,
                this.profile?.avg_chunk_length,
                this.profile?.statistics?.average_chunk_length,
                this.profile?.summary?.average_chunk_length
            ],
            0
        );
    }

    public get duplicateRate(): number {
        const value = this.firstValue([
            this.profile?.duplicate_rate,
            this.profile?.duplication_rate,
            this.profile?.statistics?.duplicate_rate,
            this.profile?.summary?.duplicate_rate
        ]);
        return this.toPercentage(value);
    }

    public get emptyTextRate(): number {
        const direct = this.firstValue([
            this.profile?.empty_text_rate,
            this.profile?.empty_rate,
            this.profile?.statistics?.empty_text_rate,
            this.profile?.statistics?.empty_rate,
            this.profile?.summary?.empty_text_rate,
            this.profile?.summary?.empty_rate
        ]);
        if (direct !== null && direct !== undefined) return this.toPercentage(direct);

        const textCoverage = this.coverageMetrics.find((item) => item.label === "원문");
        return textCoverage ? Math.max(0, 100 - textCoverage.value) : 0;
    }

    public get profileId(): string {
        return this.profile?.id || "-";
    }

    public get processingQuality(): any {
        return this.profile?.processing_quality || {};
    }

    public get morphologyQuality(): any {
        return this.processingQuality?.morphology_quality || {};
    }

    public analysisRouteLabel(value: any): string {
        const labels: { [key: string]: string } = {
            korean_morphology: '한국어 형태소',
            classical_chinese_segmentation: '한문 전용 분절',
            mixed_korean_hanja_review: '국한문 혼용 검토',
            unsupported_language_review: '언어 검토'
        };
        return labels[String(value || '')] || '확인 필요';
    }

    public get yearRangeLabel(): string {
        const range = this.profile?.publication_year_range
            || this.profile?.year_range
            || this.profile?.statistics?.publication_year_range
            || {};

        const minimum = range?.min ?? range?.from ?? range?.start ?? (Array.isArray(range) ? range[0] : null);
        const maximum = range?.max ?? range?.to ?? range?.end ?? (Array.isArray(range) ? range[1] : null);

        if (minimum === null || minimum === undefined || maximum === null || maximum === undefined) {
            return "정보 없음";
        }

        return String(minimum) + "–" + String(maximum);
    }

    public get hasProfileData(): boolean {
        return this.totalRecords > 0 || this.fields.length > 0 || this.samples.length > 0;
    }

    public formatNumber(value: number): string {
        return Number(value || 0).toLocaleString('ko-KR');
    }

    public formatPercent(value: number): string {
        const normalized = Math.max(0, Math.min(100, Number(value || 0)));
        const digits = normalized % 1 === 0 ? 0 : 1;
        return normalized.toFixed(digits) + "%";
    }

    public fieldName(field: any): string {
        return field?.name || field?.field || field?.key || "-";
    }

    public fieldType(field: any): string {
        return field?.type || field?.data_type || field?.dtype || "unknown";
    }

    public fieldCompleteness(field: any): number {
        const direct = this.firstValue([
            field?.coverage,
            field?.completeness,
            field?.non_null_rate,
            field?.present_rate
        ]);
        if (direct !== null && direct !== undefined) return this.toPercentage(direct);

        const nullRate = this.firstValue([field?.null_rate, field?.empty_rate]);
        if (nullRate !== null && nullRate !== undefined) {
            return Math.max(0, 100 - this.toPercentage(nullRate));
        }

        return 0;
    }

    public fieldSample(field: any): string {
        const value = field?.sample ?? field?.example ?? field?.sample_value;
        if (value === null || value === undefined || value === "") return "-";
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (_error) {
                return String(value);
            }
        }
        return String(value);
    }

    public fieldSamplePreview(field: any): string {
        return this.createPreview(this.fieldSample(field), 180);
    }

    public displayedFieldSample(field: any, index: number): string {
        return this.isFieldSampleExpanded(field, index)
            ? this.fieldSample(field)
            : this.fieldSamplePreview(field);
    }

    public fieldSampleExpandable(field: any): boolean {
        return this.fieldSample(field) !== this.fieldSamplePreview(field);
    }

    public isFieldSampleExpanded(field: any, index: number): boolean {
        return this.expandedTexts[this.fieldTextKey(field, index)] === true;
    }

    public async toggleFieldSample(field: any, index: number) {
        const key = this.fieldTextKey(field, index);
        this.expandedTexts[key] = !this.expandedTexts[key];
        await this.service.render();
    }

    public distributionWidth(item: DistributionItem, values: DistributionItem[]): number {
        const maximum = Math.max(...values.map((entry) => entry.count), 1);
        return Math.max(4, Math.min(100, (item.count / maximum) * 100));
    }

    public sampleTitle(sample: any): string {
        return sample?.document_title
            || sample?.title
            || sample?.document?.title
            || "문서 조각 샘플";
    }

    public sampleText(sample: any): string {
        return this.sampleFullText(sample);
    }

    public sampleFullText(sample: any): string {
        if (typeof sample === 'string') return sample;
        return String(
            sample?.full_text
            || sample?.text
            || sample?.content
            || sample?.original_text
            || "원문 없음"
        );
    }

    public samplePreview(sample: any): string {
        const provided = sample?.text_preview;
        if (provided !== undefined && provided !== null && String(provided).trim()) {
            return String(provided);
        }
        return this.createPreview(this.sampleFullText(sample));
    }

    public displayedSampleText(sample: any, index: number): string {
        return this.isSampleExpanded(sample, index)
            ? this.sampleFullText(sample)
            : this.samplePreview(sample);
    }

    public async toggleSampleText(sample: any, index: number) {
        const key = this.sampleTextKey(sample, index);
        this.expandedTexts[key] = !this.expandedTexts[key];
        await this.service.render();
    }

    public isSampleExpanded(sample: any, index: number): boolean {
        return this.expandedTexts[this.sampleTextKey(sample, index)] === true;
    }

    public sampleToggleLabel(sample: any, index: number): string {
        if (this.isSampleExpanded(sample, index)) return "접기";
        return this.isSearchExcerpt(sample) ? "발췌문 펼치기" : "전체 보기";
    }

    public isSearchExcerpt(sample: any): boolean {
        if (typeof sample === 'string') return this.selectedSourceId === 'nahf-document-archive';
        const type = String(
            sample?.content_type
            || sample?.metadata?.content_type
            || ""
        ).toLowerCase();
        const scope = String(sample?.metadata?.ingestion_scope || "").toLowerCase();
        const kind = String(sample?.metadata?.record_kind || "").toLowerCase();
        return type === "search_excerpt"
            || scope === "validation_excerpt"
            || kind === "source_chunk_excerpt";
    }

    public isCompleteText(sample: any): boolean {
        const explicit = sample?.is_complete_text ?? sample?.metadata?.is_complete_text;
        if (explicit !== undefined && explicit !== null) return explicit === true;
        return !this.isSearchExcerpt(sample) && !this.isSourceTruncated(sample);
    }

    public isSourceTruncated(sample: any): boolean {
        const explicit = sample?.is_source_truncated ?? sample?.metadata?.is_source_truncated;
        if (explicit !== undefined && explicit !== null) return explicit === true;
        return this.isSearchExcerpt(sample) || sample?.metadata?.excerpt_truncated === true;
    }

    public sampleContentLabel(sample: any): string {
        if (this.isSearchExcerpt(sample)) return "검색 발췌문";
        if (this.isCompleteText(sample)) return "전체 원문 기반 조각";
        return "불완전 원문";
    }

    public sampleContentClass(sample: any): string {
        if (this.isSearchExcerpt(sample)) return "bg-amber-50 text-amber-800 ring-amber-600/20";
        if (this.isCompleteText(sample)) return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
        return "bg-slate-100 text-slate-700 ring-slate-500/20";
    }

    public sampleForcedSplit(sample: any): boolean {
        return (sample?.forced_split ?? sample?.metadata?.forced_split) === true;
    }

    public sampleBoundaryLabel(sample: any): string {
        if (this.sampleForcedSplit(sample)) return "강제 분할됨";
        if (this.isSearchExcerpt(sample)) return "문장 경계 미검증";
        return "문장 경계에서 정상 분할";
    }

    public sampleBoundaryClass(sample: any): string {
        return this.sampleForcedSplit(sample)
            ? "bg-red-50 text-red-700 ring-red-600/20"
            : "bg-sky-50 text-sky-700 ring-sky-600/20";
    }

    public sampleExtractionUsable(sample: any): boolean {
        const explicit = sample?.knowledge_extraction_usable
            ?? sample?.knowledge_extraction_eligible
            ?? sample?.extraction_eligible
            ?? sample?.metadata?.knowledge_extraction_usable
            ?? sample?.metadata?.knowledge_extraction_eligible
            ?? sample?.metadata?.extraction_eligible;
        if (explicit !== undefined && explicit !== null) return explicit === true;
        return this.isCompleteText(sample) && !this.isSearchExcerpt(sample);
    }

    public sampleLimitationReason(sample: any): string {
        if (!this.isSearchExcerpt(sample)) return "";
        return sample?.limitation_reason
            || sample?.metadata?.limitation_reason
            || "외부 API가 전체 원문이 아닌 제한된 검색 발췌문만 제공함";
    }

    public sampleMeta(sample: any): string {
        if (typeof sample === 'string') return "원문 샘플";

        const values: string[] = [];
        const page = sample?.page ?? sample?.page_number;
        const index = sample?.chunk_index ?? sample?.index;
        const language = sample?.language || sample?.metadata?.language;

        if (page !== undefined && page !== null && page !== "") values.push("페이지 " + page);
        if (index !== undefined && index !== null && index !== "") values.push("순서 " + index);
        if (language) values.push(String(language));

        return values.length > 0 ? values.join(" · ") : "문서 조각";
    }

    private normalizeFields(value: any): any[] {
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.items)) return value.items;
        if (!value || typeof value !== 'object') return [];

        return Object.keys(value).map((name) => {
            const detail = value[name];
            if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
                return { name: name, ...detail };
            }
            return { name: name, type: detail };
        });
    }

    private normalizeCoverage(value: any): CoverageMetric[] {
        if (Array.isArray(value)) {
            return value.map((item) => ({
                label: item?.label || item?.name || item?.field || "항목",
                description: item?.description || "값이 존재하는 레코드 비율",
                value: this.toPercentage(item?.value ?? item?.rate ?? item?.coverage)
            }));
        }

        const source = value && typeof value === 'object' ? value : {};
        const definitions = [
            {
                label: "원문",
                description: "원문 텍스트를 가진 문서 조각",
                keys: ["text", "text_rate", "text_coverage", "content"]
            },
            {
                label: "문서 ID",
                description: "원본 문서를 식별할 수 있는 레코드",
                keys: ["document_id", "document_id_rate", "document", "external_id"]
            },
            {
                label: "페이지",
                description: "페이지 위치를 가진 문서 조각",
                keys: ["page", "page_rate", "page_coverage"]
            },
            {
                label: "출처 정보",
                description: "제목·저자 등 출처를 가진 문서",
                keys: ["source", "source_rate", "source_coverage", "provenance"]
            }
        ];

        return definitions.map((definition) => ({
            label: definition.label,
            description: definition.description,
            value: this.toPercentage(this.objectValue(source, definition.keys))
        }));
    }

    private normalizeDistribution(value: any): DistributionItem[] {
        if (Array.isArray(value)) {
            return value.map((item) => {
                if (typeof item !== 'object' || item === null) {
                    return { label: String(item), count: 1 };
                }

                return {
                    label: String(
                        item.label
                            || item.name
                            || item.language
                            || item.country
                            || item.key
                            || "기타"
                    ),
                    count: this.pickNumber(
                        [item.count, item.value, item.total, item.records],
                        0
                    )
                };
            }).filter((item) => item.count >= 0);
        }

        if (!value || typeof value !== 'object') return [];

        return Object.keys(value).map((key) => ({
            label: key,
            count: this.pickNumber([value[key]?.count, value[key]?.value, value[key]], 0)
        }));
    }

    private summaryCount(keys: string[], fallback: number): number {
        const scopes = [
            this.profile,
            this.profile?.summary,
            this.profile?.counts,
            this.profile?.statistics
        ];

        for (const scope of scopes) {
            if (!scope || Array.isArray(scope)) continue;
            for (const key of keys) {
                if (scope[key] !== undefined && scope[key] !== null) {
                    return this.toCount(scope[key]);
                }
            }
        }

        return fallback;
    }

    private objectValue(source: any, keys: string[]): any {
        for (const key of keys) {
            if (source?.[key] !== undefined && source?.[key] !== null) {
                return source[key];
            }
        }
        return 0;
    }

    private toCount(value: any): number {
        if (Array.isArray(value)) return value.length;
        if (value && typeof value === 'object') {
            if (value.count !== undefined) return this.toCount(value.count);
            if (value.total !== undefined) return this.toCount(value.total);
        }

        const numberValue = Number(value || 0);
        return Number.isFinite(numberValue) ? numberValue : 0;
    }

    private toPercentage(value: any): number {
        const numberValue = Number(value || 0);
        if (!Number.isFinite(numberValue)) return 0;

        const percent = numberValue >= 0 && numberValue <= 1
            ? numberValue * 100
            : numberValue;
        return Math.max(0, Math.min(100, percent));
    }

    private pickNumber(values: any[], fallback: number): number {
        const value = this.firstValue(values);
        if (value === null || value === undefined) return fallback;

        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : fallback;
    }

    private firstValue(values: any[]): any {
        for (const value of values) {
            if (value !== undefined && value !== null && value !== "") return value;
        }
        return null;
    }

    private toArray(value: any): any[] {
        if (Array.isArray(value)) return value;
        if (Array.isArray(value?.items)) return value.items;
        if (Array.isArray(value?.rows)) return value.rows;
        return [];
    }

    private fieldTextKey(field: any, index: number): string {
        return this.selectedSourceId + ":field:" + String(this.fieldName(field) || index);
    }

    private sampleTextKey(sample: any, index: number): string {
        const id = sample?.id || sample?.chunk_id || sample?.external_id || index;
        return this.selectedSourceId + ":profile-sample:" + String(id);
    }

    private createPreview(value: string, maximum: number = 240): string {
        const text = String(value || "");
        if (text.length <= maximum) return text;

        const candidate = text.slice(0, maximum);
        const boundaries = [
            candidate.lastIndexOf("\n"),
            candidate.lastIndexOf("다. "),
            candidate.lastIndexOf(". "),
            candidate.lastIndexOf("。"),
            candidate.lastIndexOf("! "),
            candidate.lastIndexOf("? ")
        ];
        const boundary = Math.max(...boundaries);
        const end = boundary >= Math.floor(maximum * 0.55) ? boundary + 1 : maximum;
        return candidate.slice(0, end).trimEnd() + "…";
    }

    private responseMessage(data: any, fallback: string): string {
        return typeof data === 'string' ? data : data?.message || fallback;
    }
}

export default PageDataProfileComponent;